import { Component, For, Show } from "solid-js";
import { unlockAmbient } from "../lib/ambient-audio";
import {
    enableManualSpeedOverride,
    pauseAnimation,
    resetAnimation,
    skipSeconds,
    startAnimation,
} from "../lib/animation";
import { unlockTimelineAudio } from "../lib/timeline-audio";
import { handleViewTransition } from "../lib/view-transition";
import { useAppStore } from "../stores/app";
import { clock, setSpeedMultiplier } from "../stores/clock";
import { useScenarioStore } from "../stores/scenario";

interface ClockControlsProps {
	onPlay: () => void;
	onReset?: () => void;
}

const SPEED_OPTIONS = [1, 2, 5, 10, 20, 50];

const ClockControls: Component<ClockControlsProps> = (props) => {
	const [scenario] = useScenarioStore();
	const [app] = useAppStore();
	let skipIntervalId: number | null = null;

	const canStart = () =>
		scenario.agents.length > 0 &&
		scenario.agents.some((a) => a.segments.some((s) => s.waypoints.length >= 2));

	const handlePlay = () => {
		if (!clock.isRunning && canStart()) {
			// Unlock audio on play button click
			unlockAmbient();
			unlockTimelineAudio();
			props.onPlay();
			startAnimation();
		}
	};

	const handleSpeedChange = (speed: number) => {
		setSpeedMultiplier(speed);
		if (clock.isRunning) {
			enableManualSpeedOverride();
		}
	};

	// Hold-to-repeat skip functionality — uses Pointer Events so the browser
	// knows we own the gesture (suppresses iOS magnifier / callout / selection).
	const startSkipRepeat = (delta: number, e: PointerEvent) => {
		if (!e.isPrimary) return
		e.preventDefault()
		// Capture the pointer so we keep receiving events even if the finger drifts off the button
		;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
		handleViewTransition(() => skipSeconds(delta))
		skipIntervalId = window.setInterval(() => {
			handleViewTransition(() => skipSeconds(delta))
		}, 500)
	}

	const stopSkipRepeat = () => {
		if (skipIntervalId !== null) {
			clearInterval(skipIntervalId)
			skipIntervalId = null
		}
	}

	return (
		<div class="flex flex-col bg-gray-900 text-white border-t border-gray-700 select-none">
			{/* Mobile layout - compact icon-only controls */}
			<Show when={app.isMobile}>
				{/* Speed controls - always visible on mobile */}
				<div class="relative flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700 select-none">
					<span class="text-xs text-gray-400">Hastighet:</span>
					<For each={SPEED_OPTIONS}>
						{(speed) => (
							<button
								class={`px-2 py-1 rounded text-xs font-medium transition-colors touch-manipulation select-none ${
									clock.speedMultiplier === speed
										? "bg-blue-600 text-white"
										: "bg-gray-700 text-gray-300 active:bg-gray-600"
								}`}
								style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
								onClick={() => handleSpeedChange(speed)}
								onContextMenu={(e) => e.preventDefault()}
							>
								{speed}x
							</button>
						)}
					</For>
				</div>
				{/* Main controls - single row */}
				<div
					class="flex items-center justify-center w-full gap-3 px-4 pt-2 select-none"
					style={{ "padding-bottom": "max(0.5rem, calc(env(safe-area-inset-bottom) / 4))" }}
				>
					{/* Skip back button */}
					<button
						class="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 active:bg-gray-800 text-white transition-all flex items-center justify-center select-none touch-none active:scale-105"
						style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
						ref={(el) => el.addEventListener('pointerdown', (e) => startSkipRepeat(-30, e), { passive: false })}
						onPointerUp={stopSkipRepeat}
						onPointerCancel={stopSkipRepeat}
						onPointerLeave={stopSkipRepeat}
						onContextMenu={(e) => e.preventDefault()}
						title="Hoppa tillbaka 30 sekunder"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="w-5 h-5 pointer-events-none select-none"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
							/>
						</svg>
					</button>
					<button
						class={`w-10 h-10 rounded-lg transition-colors flex items-center justify-center touch-manipulation select-none ${
							clock.isRunning
								? "bg-gray-600 text-gray-400 cursor-not-allowed"
								: canStart()
									? "bg-green-600 hover:bg-green-500 text-white active:bg-green-700"
									: "bg-gray-600 text-gray-400 cursor-not-allowed"
						}`}
						style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
						onClick={handlePlay}
						onContextMenu={(e) => e.preventDefault()}
						disabled={clock.isRunning || !canStart()}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="w-5 h-5"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
							/>
						</svg>
					</button>
					<button
						class={`w-10 h-10 rounded-lg transition-colors flex items-center justify-center touch-manipulation select-none ${
							clock.isRunning
								? "bg-amber-500 hover:bg-amber-400 text-white active:bg-amber-400"
								: "bg-gray-600 text-gray-400 cursor-not-allowed"
						}`}
						style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
						onClick={() => pauseAnimation()}
						onContextMenu={(e) => e.preventDefault()}
						disabled={!clock.isRunning}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="w-5 h-5"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M15.75 5.25v13.5m-7.5-13.5v13.5"
							/>
						</svg>
					</button>
					<button
						class="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 active:bg-gray-800 text-white transition-colors flex items-center justify-center touch-manipulation select-none"
						style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
					onClick={() => props.onReset ? props.onReset() : resetAnimation()}
						onContextMenu={(e) => e.preventDefault()}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="w-5 h-5"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
							/>
						</svg>
					</button>
					{/* Skip forward button */}
					<button
						class="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 active:bg-gray-800 text-white transition-all flex items-center justify-center select-none touch-none active:scale-105"
						style={{ "-webkit-tap-highlight-color": "transparent", "-webkit-touch-callout": "none", "-webkit-user-select": "none" }}
						ref={(el) => el.addEventListener('pointerdown', (e) => startSkipRepeat(30, e), { passive: false })}
						onPointerUp={stopSkipRepeat}
						onPointerCancel={stopSkipRepeat}
						onPointerLeave={stopSkipRepeat}
						onContextMenu={(e) => e.preventDefault()}
						title="Hoppa fram 30 sekunder"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							class="w-5 h-5 pointer-events-none select-none"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="m15 15 6-6m0 0-6-6m6 6H9a6 6 0 0 0 0 12h3"
							/>
						</svg>
					</button>
				</div>
			</Show>

			{/* Desktop layout - full controls */}
			<Show when={!app.isMobile}>
				<div class="flex items-center justify-between px-4 py-1.5">
					<div class="flex items-center gap-2">
						<button
							class={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
								clock.isRunning
									? "bg-gray-600 text-gray-400 cursor-not-allowed"
									: canStart()
										? "bg-green-600 hover:bg-green-500 text-white"
										: "bg-gray-600 text-gray-400 cursor-not-allowed"
							}`}
							onClick={handlePlay}
							disabled={clock.isRunning || !canStart()}
						>
							▶ Play
						</button>
						<button
							class={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
								clock.isRunning
									? "bg-amber-500 hover:bg-amber-400 text-white"
									: "bg-gray-600 text-gray-400 cursor-not-allowed"
							}`}
							onClick={() => pauseAnimation()}
							disabled={!clock.isRunning}
						>
							⏸ Pause
						</button>
						<button
							class="px-4 py-1.5 rounded text-sm font-medium bg-gray-700 hover:bg-gray-600 text-white transition-colors"
						onClick={() => props.onReset ? props.onReset() : resetAnimation()}
					>
						↺ Restart
						</button>

						{/* Skip buttons */}
						<button
							class="px-2 py-1.5 rounded text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 transition-all select-none touch-none active:scale-105"
						ref={(el) => el.addEventListener('pointerdown', (e) => startSkipRepeat(-30, e), { passive: false })}
							onPointerUp={stopSkipRepeat}
							onPointerCancel={stopSkipRepeat}
							onPointerLeave={stopSkipRepeat}
							onContextMenu={(e) => e.preventDefault()}
							title="Hoppa tillbaka 30 sekunder (håll inne för att upprepa)"
						>
							-30s
						</button>
						<button
							class="px-2 py-1.5 rounded text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 transition-all select-none touch-none active:scale-105"
						ref={(el) => el.addEventListener('pointerdown', (e) => startSkipRepeat(30, e), { passive: false })}
							onPointerUp={stopSkipRepeat}
							onPointerCancel={stopSkipRepeat}
							onPointerLeave={stopSkipRepeat}
							onContextMenu={(e) => e.preventDefault()}
							title="Hoppa fram 30 sekunder (håll inne för att upprepa)"
						>
							+30s
						</button>
					</div>

				<div class="flex items-center gap-2">
					<span class="text-xs text-gray-400">Hastighet:</span>
					<For each={SPEED_OPTIONS}>
						{(speed) => (
							<button
								class={`px-2 py-1 rounded text-xs font-medium transition-colors ${
									clock.speedMultiplier === speed
										? "bg-blue-600 text-white"
										: "bg-gray-700 text-gray-300 hover:bg-gray-600"
								}`}
								onClick={() => handleSpeedChange(speed)}
							>
								{speed}x
							</button>
						)}
					</For>
				</div>					<div class="text-xs text-gray-400">
						{scenario.agents.length} aktör{scenario.agents.length !== 1 ? "er" : ""}
					</div>
				</div>
			</Show>
		</div>
	);
};

export default ClockControls;
